import {
  categoryIndicators,
  mapRiskName,
  riskIndicatorGroupings,
  type RiskIndicator,
} from "@/revenue-intelligence/lib/risk-contracts";
import {
  getRiskBucket,
  type StateOfSalesV2ForecastMovement,
  type StateOfSalesV2ForecastMovementCategoryKey,
  type StateOfSalesV2ForecastMovementDeal,
  type StateOfSalesV2LossSignalGroup,
} from "../lib/risk";
import type { StateOfSalesV2ForecastPosition } from "./forecastPosition";
import {
  buildManagementAttentionShortlist,
  type ManagementAttentionCandidate,
} from "./forecastManagementAttention";

export type ForecastInsightDeal = {
  adjustedCloseDate?: Date | string | null;
  forecast_contribution?: number | null;
  forecast_share?: number | null;
  id: number | string;
  name?: string | null;
  risk_score?: number | null;
  risks?: Partial<Record<RiskIndicator, { risk?: number | null }>>;
  urgency_bucket?: "high" | "medium" | "low";
  urgency_score?: number | null;
  value?: number | null;
  weighted_risk_score?: number | null;
};

export type ForecastCurrentSignalEvidence<T extends ForecastInsightDeal = ForecastInsightDeal> = {
  affectedDealCount: number;
  dealValue: number;
  deals: T[];
  forecastShare: number | null;
  forecastValue: number;
  helperRank: number;
  indicator: RiskIndicator;
  label: string;
  topDealName: string | null;
  topDealShare: number | null;
};

export type ForecastLossPatternEvidence = {
  affectedDealCount: number;
  affectedDealShare: number | null;
  dealIds: string[];
  group: StateOfSalesV2LossSignalGroup["group"];
  historicallyEvaluatedDealCount: number;
  label: string;
  missingHistoricalDealCount: number;
  openDealCount: number;
  openDealShare: number | null;
  openDealTotal: number;
  recentLostDealCount: number;
};

export type ForecastConcentrationKey = "pipeline" | "sales_rep" | "stage" | "team";

export type ForecastConcentration<T extends ForecastInsightDeal = ForecastInsightDeal> = {
  dealCount: number;
  deals: T[];
  dimension: string;
  key: ForecastConcentrationKey;
  label: string;
  share: number;
  value: number;
};

export type ForecastDealDependency<T extends ForecastInsightDeal = ForecastInsightDeal> =
  Omit<ForecastConcentration<T>, "key"> & { key: "deals" };

export type ForecastPositionInsight = {
  difference: number;
  expectedFromOpenDeals: number;
  forecast: number;
  historical: boolean;
  status: "above_target" | "below_target" | "on_target" | "target_unavailable";
  target: number;
  won: number;
};

export type ForecastMovementCategoryInsight = {
  amount: number;
  dealCount: number;
  deals: StateOfSalesV2ForecastMovementDeal[];
  direction: "negative" | "positive" | "zero";
  key: StateOfSalesV2ForecastMovementCategoryKey | "unclassified";
  label: string;
};

export type ForecastMovementInsight = {
  amount: number;
  categories: ForecastMovementCategoryInsight[];
  direction: "decrease" | "increase" | "stable";
  dominantDriver: ForecastMovementCategoryInsight | null;
  dominantDriverState:
    | "dominant_negative"
    | "dominant_positive"
    | "driver_data_unavailable"
    | "multiple_equal_drivers"
    | "no_meaningful_driver";
  dominantNegativeDriver: ForecastMovementCategoryInsight | null;
  dominantPositiveDriver: ForecastMovementCategoryInsight | null;
  negativeMovement: number;
  positiveMovement: number;
  state: "decrease" | "increase" | "insufficient_history" | "offset" | "stable";
};

export type ForecastManagementAction<T extends ForecastInsightDeal = ForecastInsightDeal> =
  ManagementAttentionCandidate<T>;

export type ForecastL2Insights<T extends ForecastInsightDeal = ForecastInsightDeal> = {
  currentSignals: {
    dominant: ForecastCurrentSignalEvidence<T> | null;
    rows: ForecastCurrentSignalEvidence<T>[];
    state: "insufficient_signal_data" | "one_dominant_exposure" | "several_equal_exposures";
  };
  lossPatterns: {
    dominant: ForecastLossPatternEvidence | null;
    rows: ForecastLossPatternEvidence[];
    sameDealPopulation: boolean;
    state:
      | "insufficient_signal_data"
      | "insufficient_signal_history"
      | "no_significant_signal"
      | "one_dominant_pattern"
      | "several_equal_patterns";
  };
  managementAttention: {
    actions: ForecastManagementAction<T>[];
    candidates: ForecastManagementAction<T>[];
    eligibleCandidates: ForecastManagementAction<T>[];
    state: "multiple_priorities" | "no_immediate_priority" | "one_priority";
    suppressedCandidateIds: string[];
  };
  movement: ForecastMovementInsight;
  position: ForecastPositionInsight;
};

const movementLabels: Record<StateOfSalesV2ForecastMovementCategoryKey, string> = {
  new_deals: "New deals",
  closed_won: "Won deals",
  closed_lost: "Lost deals",
  moved_in: "Moved in",
  moved_out: "Moved out",
  improved_outlook: "Improved outlook",
  weakened_outlook: "Weakened outlook",
  unclassified: "Unclassified change",
};

function getRiskIndicatorScore(deal: ForecastInsightDeal, indicator: RiskIndicator) {
  const score = Number(deal.risks?.[indicator]?.risk ?? 0);
  return Number.isFinite(score) ? score : 0;
}

export function getDealForecastContribution(deal: ForecastInsightDeal) {
  const contribution = Number(deal.forecast_contribution ?? 0);
  return Number.isFinite(contribution) ? contribution : 0;
}

export function getDealsForForecastSignal<T extends ForecastInsightDeal>(
  deals: T[],
  indicator: RiskIndicator,
) {
  return Array.from(new Map(deals.map(deal => [String(deal.id), deal])).values())
    .filter(deal => getDealForecastContribution(deal) > 0)
    .filter((deal) => getRiskBucket(getRiskIndicatorScore(deal, indicator)) !== "low")
    .toSorted((a, b) => {
      const indicatorScoreDifference = getRiskIndicatorScore(b, indicator)
        - getRiskIndicatorScore(a, indicator);
      if (indicatorScoreDifference !== 0) return indicatorScoreDifference;

      const forecastRiskDifference = Number(b.weighted_risk_score ?? 0)
        - Number(a.weighted_risk_score ?? 0);
      if (forecastRiskDifference !== 0) return forecastRiskDifference;

      return Number(b.value ?? 0) - Number(a.value ?? 0);
    });
}

function getEnteredDealValue(deal: ForecastInsightDeal) {
  const value = Number(deal.value ?? 0);
  return Number.isFinite(value) ? value : 0;
}

export function buildForecastCurrentSignalEvidence<T extends ForecastInsightDeal>(
  topDrivers: Array<{ risk: string }>,
  deals: T[],
  totalForecast: number,
): ForecastCurrentSignalEvidence<T>[] {
  return topDrivers.flatMap((driver, helperRank) => {
    if (!categoryIndicators.forecast.includes(driver.risk as RiskIndicator)) return [];
    const indicator = driver.risk as RiskIndicator;
    const affectedDeals = getDealsForForecastSignal(deals, indicator);
    if (!affectedDeals.length) return [];
    const forecastValue = affectedDeals.reduce(
      (sum, deal) => sum + getDealForecastContribution(deal),
      0,
    );
    const dealValue = affectedDeals.reduce(
      (sum, deal) => sum + getEnteredDealValue(deal),
      0,
    );
    const topDeal = affectedDeals
      .toSorted((a, b) => getEnteredDealValue(b) - getEnteredDealValue(a))
      .at(0) ?? null;

    return [{
      affectedDealCount: affectedDeals.length,
      dealValue,
      deals: affectedDeals,
      forecastShare: totalForecast > 0 ? forecastValue / totalForecast : null,
      forecastValue,
      helperRank,
      indicator,
      label: mapRiskName(indicator),
      topDealName: topDeal?.name ?? null,
      topDealShare: topDeal && dealValue > 0
        ? getEnteredDealValue(topDeal) / dealValue
        : null,
    }];
  });
}

export function buildForecastLossPatternEvidence(
  groups: StateOfSalesV2LossSignalGroup[],
  openDeals: ForecastInsightDeal[],
): ForecastLossPatternEvidence[] {
  const recentLostDealIds = new Set(
    groups.flatMap(group => group.deals.map(deal => String(deal.id))),
  );

  return groups.map(group => {
    const groupIndicators = riskIndicatorGroupings[group.group];
    const openDealCount = openDeals.filter(deal => groupIndicators.some(
      indicator => getRiskBucket(getRiskIndicatorScore(deal, indicator)) !== "low",
    )).length;

    return {
      affectedDealCount: group.affectedDealCount,
      affectedDealShare: group.recentLostDealCount > 0
        ? group.affectedDealCount / group.recentLostDealCount
        : null,
      dealIds: group.deals.map(deal => String(deal.id)),
      group: group.group,
      historicallyEvaluatedDealCount: group.historicallyEvaluatedDealCount,
      label: group.label,
      missingHistoricalDealCount: group.missingHistoricalDealCount,
      openDealCount,
      openDealShare: openDeals.length > 0 ? openDealCount / openDeals.length : null,
      openDealTotal: openDeals.length,
      recentLostDealCount: group.recentLostDealCount || recentLostDealIds.size,
    };
  });
}

export function getTopForecastConcentration<T extends ForecastInsightDeal>(
  deals: T[],
  totalForecast: number,
  key: ForecastConcentrationKey,
  dimension: string,
  getLabel: (deal: T) => string,
): ForecastConcentration<T> | null {
  if (totalForecast <= 0) return null;
  const groups = new Map<string, { deals: T[]; label: string; value: number }>();

  const contributingDeals = Array.from(
    new Map(deals.map(deal => [String(deal.id), deal])).values(),
  ).filter(deal => getDealForecastContribution(deal) > 0);

  for (const deal of contributingDeals) {
    const label = getLabel(deal).trim() || "Not specified";
    const current = groups.get(label) ?? { deals: [], label, value: 0 };
    groups.set(label, {
      deals: [...current.deals, deal],
      label,
      value: current.value + getDealForecastContribution(deal),
    });
  }

  const rows = Array.from(groups.values());
  if (rows.length <= 1) return null;
  const specifiedRows = rows.filter(row => ![
    "not specified",
    "other",
    "unclassified",
  ].includes(row.label.toLowerCase()));
  const top = (specifiedRows.length ? specifiedRows : rows)
    .toSorted((a, b) => b.value - a.value)
    .at(0) ?? null;
  if (!top) return null;

  return {
    dealCount: top.deals.length,
    deals: top.deals,
    dimension,
    key,
    label: top.label,
    share: top.value / totalForecast,
    value: top.value,
  };
}

export function getTopForecastDealDependency<T extends ForecastInsightDeal>(
  deals: T[],
  totalForecast: number,
  limit = 3,
): ForecastDealDependency<T> | null {
  if (totalForecast <= 0 || limit <= 0) return null;
  const uniqueDeals = new Map<string, T>();
  for (const deal of deals) {
    const id = String(deal.id);
    if (!uniqueDeals.has(id)) uniqueDeals.set(id, deal);
  }
  const topDeals = Array.from(uniqueDeals.values())
    .filter(deal => getDealForecastContribution(deal) > 0)
    .toSorted((a, b) => getDealForecastContribution(b) - getDealForecastContribution(a))
    .slice(0, limit);
  if (!topDeals.length) return null;

  const value = topDeals.reduce(
    (sum, deal) => sum + getDealForecastContribution(deal),
    0,
  );
  const firstName = topDeals[0].name?.trim() || "Largest Deal";

  return {
    dealCount: topDeals.length,
    deals: topDeals,
    dimension: "Key Deals",
    key: "deals",
    label: topDeals.length === 1 ? firstName : `${firstName} + ${topDeals.length - 1} more`,
    share: value / totalForecast,
    value,
  };
}

export function getTopForecastMembershipConcentration<T extends ForecastInsightDeal>(
  deals: T[],
  totalForecast: number,
  key: "team",
  dimension: string,
  memberships: Array<{ dealIds: Array<number | string>; id: number | string; label: string }>,
): ForecastConcentration<T> | null {
  if (totalForecast <= 0 || deals.length === 0) return null;
  const dealById = new Map(
    deals
      .filter(deal => getDealForecastContribution(deal) > 0)
      .map(deal => [String(deal.id), deal]),
  );
  if (dealById.size === 0) return null;
  const uniqueDealSets = new Set<string>();
  const rows = memberships.flatMap((membership) => {
    const memberDeals = Array.from(new Set(membership.dealIds.map(String)))
      .flatMap(dealId => dealById.get(dealId) ?? []);
    if (!memberDeals.length) return [];
    const dealSetKey = memberDeals.map(deal => String(deal.id)).toSorted().join("|");
    if (uniqueDealSets.has(dealSetKey)) return [];
    uniqueDealSets.add(dealSetKey);
    const value = memberDeals.reduce(
      (sum, deal) => sum + getDealForecastContribution(deal),
      0,
    );
    const share = value / totalForecast;
    if (share >= 0.999_999) return [];
    return [{
      dealCount: memberDeals.length,
      deals: memberDeals,
      dimension,
      key,
      label: membership.label.trim() || "Not specified",
      share,
      value,
    }];
  });

  if (rows.length <= 1) return null;
  return rows.toSorted((a, b) => b.value - a.value).at(0) ?? null;
}

function buildForecastPositionInsight(
  position: StateOfSalesV2ForecastPosition,
): ForecastPositionInsight {
  if (position.target <= 0) {
    return {
      difference: 0,
      expectedFromOpenDeals: position.expectedFromOpenDeals,
      forecast: position.forecast,
      historical: position.historical,
      status: "target_unavailable",
      target: position.target,
      won: position.won,
    };
  }

  return {
    difference: Math.abs(position.gapToTarget),
    expectedFromOpenDeals: position.expectedFromOpenDeals,
    forecast: position.forecast,
    historical: position.historical,
    status: position.gapToTarget > 0
      ? "above_target"
      : position.gapToTarget < 0
        ? "below_target"
        : "on_target",
    target: position.target,
    won: position.won,
  };
}

function buildForecastMovementInsight(
  movement: StateOfSalesV2ForecastMovement | null | undefined,
  currentForecast: number,
): ForecastMovementInsight {
  if (!movement?.available) {
    return {
      amount: 0,
      categories: [],
      direction: "stable",
      dominantDriver: null,
      dominantDriverState: "driver_data_unavailable",
      dominantNegativeDriver: null,
      dominantPositiveDriver: null,
      negativeMovement: 0,
      positiveMovement: 0,
      state: "insufficient_history",
    };
  }

  const values: Record<StateOfSalesV2ForecastMovementCategoryKey, number> = {
    new_deals: movement.values.newDeals,
    closed_won: movement.values.closedWon,
    closed_lost: movement.values.closedLost,
    moved_in: movement.values.movedIn,
    moved_out: movement.values.movedOut,
    improved_outlook: movement.values.improvedOutlook,
    weakened_outlook: movement.values.weakenedOutlook,
    unclassified: currentForecast - movement.startForecast
      - movement.values.newDeals
      - movement.values.closedWon
      - movement.values.closedLost
      - movement.values.movedIn
      - movement.values.movedOut
      - movement.values.improvedOutlook
      - movement.values.weakenedOutlook,
  };
  const categories = (Object.keys(values) as StateOfSalesV2ForecastMovementCategoryKey[])
    .filter(key => values[key] !== 0 || movement.dealsByCategory[key].length > 0)
    .map(key => ({
      amount: values[key],
      dealCount: movement.dealsByCategory[key].length,
      deals: movement.dealsByCategory[key],
      direction: values[key] > 0
        ? "positive" as const
        : values[key] < 0
          ? "negative" as const
          : "zero" as const,
      key,
      label: movementLabels[key],
    }));
  const positiveMovement = categories.reduce(
    (sum, category) => sum + (category.amount > 0 ? category.amount : 0),
    0,
  );
  const negativeMovement = categories.reduce(
    (sum, category) => sum + (category.amount < 0 ? Math.abs(category.amount) : 0),
    0,
  );
  const netMovement = currentForecast - movement.startForecast;
  const supportedCategories = categories.filter(
    category => category.key !== "unclassified" && category.amount !== 0,
  );
  const dominantPositiveDriver = supportedCategories
    .filter(category => category.amount > 0)
    .toSorted((a, b) => b.amount - a.amount)
    .at(0) ?? null;
  const dominantNegativeDriver = supportedCategories
    .filter(category => category.amount < 0)
    .toSorted((a, b) => Math.abs(b.amount) - Math.abs(a.amount))
    .at(0) ?? null;
  const rankedSupportedDrivers = supportedCategories
    .toSorted((a, b) => Math.abs(b.amount) - Math.abs(a.amount));
  const candidateDominantDriver = rankedSupportedDrivers.at(0) ?? null;
  const unclassified = categories.find(category => category.key === "unclassified") ?? null;
  const unclassifiedIsLargest = Boolean(
    unclassified
    && (!candidateDominantDriver
      || Math.abs(unclassified.amount) > Math.abs(candidateDominantDriver.amount)),
  );
  const hasEqualSupportedDrivers = Boolean(
    candidateDominantDriver
    && rankedSupportedDrivers.at(1)
    && Math.abs(candidateDominantDriver.amount) === Math.abs(rankedSupportedDrivers[1].amount),
  );
  const dominantDriver = unclassifiedIsLargest || hasEqualSupportedDrivers
    ? null
    : candidateDominantDriver;
  const dominantDriverState = unclassifiedIsLargest
    ? "driver_data_unavailable" as const
    : hasEqualSupportedDrivers
      ? "multiple_equal_drivers" as const
      : dominantDriver
        ? dominantDriver.amount > 0
          ? "dominant_positive" as const
          : "dominant_negative" as const
        : "no_meaningful_driver" as const;

  return {
    amount: Math.abs(netMovement),
    categories,
    direction: netMovement > 0 ? "increase" : netMovement < 0 ? "decrease" : "stable",
    dominantDriver,
    dominantDriverState,
    dominantNegativeDriver,
    dominantPositiveDriver,
    negativeMovement,
    positiveMovement,
    state: netMovement === 0
      ? "stable"
      : positiveMovement > 0 && negativeMovement > 0
        ? "offset"
        : netMovement > 0
          ? "increase"
          : "decrease",
  };
}

function getSignalState<T extends ForecastInsightDeal>(
  rows: ForecastCurrentSignalEvidence<T>[],
): ForecastL2Insights<T>["currentSignals"]["state"] {
  if (!rows.length) return "insufficient_signal_data";
  if (rows.length > 1 && rows[0].dealValue === rows[1].dealValue) {
    return "several_equal_exposures";
  }
  return "one_dominant_exposure";
}

function getLossPatternState(
  rows: ForecastLossPatternEvidence[],
): ForecastL2Insights["lossPatterns"]["state"] {
  const recentLostDealCount = rows.at(0)?.recentLostDealCount ?? 0;
  const historicallyEvaluatedDealCount = rows.at(0)?.historicallyEvaluatedDealCount ?? 0;
  if (recentLostDealCount > 0 && historicallyEvaluatedDealCount === 0) {
    return "insufficient_signal_history";
  }
  const populatedRows = rows.filter(row => row.affectedDealCount > 0);
  if (!populatedRows.length) {
    return recentLostDealCount > 0
      ? "no_significant_signal"
      : "insufficient_signal_data";
  }
  if (
    populatedRows.length > 1
    && populatedRows[0].affectedDealCount === populatedRows[1].affectedDealCount
  ) {
    return "several_equal_patterns";
  }
  return "one_dominant_pattern";
}

export function buildForecastL2Insights<T extends ForecastInsightDeal>({
  concentrations,
  currentSignals,
  dependencyDeals,
  lossPatterns,
  managementSignals,
  movement,
  position,
  selectedPeriodRange,
  urgentDeals,
}: {
  concentrations: ForecastConcentration<T>[];
  currentSignals: ForecastCurrentSignalEvidence<T>[];
  dependencyDeals: T[];
  lossPatterns: ForecastLossPatternEvidence[];
  managementSignals?: ForecastCurrentSignalEvidence<T>[];
  movement: StateOfSalesV2ForecastMovement | null | undefined;
  position: StateOfSalesV2ForecastPosition;
  selectedPeriodRange?: { from: Date | string; to: Date | string } | null;
  urgentDeals: T[];
}): ForecastL2Insights<T> {
  const sortedCurrentSignals = currentSignals.toSorted((a, b) => (
    b.dealValue - a.dealValue
    || a.helperRank - b.helperRank
  ));
  const sortedLossPatterns = lossPatterns.toSorted((a, b) => (
    b.affectedDealCount - a.affectedDealCount
  ));
  const populatedLossPatterns = sortedLossPatterns.filter(row => row.affectedDealCount > 0);
  const sameDealPopulation = populatedLossPatterns.length > 1
    && new Set(populatedLossPatterns.map(row => row.dealIds.toSorted().join("|"))).size === 1;
  const managementAttention = buildManagementAttentionShortlist({
    concentrations,
    currentSignals: managementSignals ?? sortedCurrentSignals,
    dependencyDeals,
    movement,
    selectedPeriodRange,
    target: position.target > 0 ? position.target : null,
    totalForecast: position.forecast,
    urgentDeals,
  });
  const managementState = managementAttention.selectedCandidates.length > 1
    ? "multiple_priorities" as const
    : managementAttention.selectedCandidates.length === 1
      ? "one_priority" as const
      : "no_immediate_priority" as const;

  return {
    currentSignals: {
      dominant: sortedCurrentSignals.at(0) ?? null,
      rows: sortedCurrentSignals,
      state: getSignalState(sortedCurrentSignals),
    },
    lossPatterns: {
      dominant: sortedLossPatterns.find(row => row.affectedDealCount > 0) ?? null,
      rows: sortedLossPatterns,
      sameDealPopulation,
      state: getLossPatternState(sortedLossPatterns),
    },
    managementAttention: {
      actions: managementAttention.selectedCandidates,
      candidates: managementAttention.candidates,
      eligibleCandidates: managementAttention.eligibleCandidates,
      state: managementState,
      suppressedCandidateIds: managementAttention.suppressedCandidateIds,
    },
    movement: buildForecastMovementInsight(movement, position.forecast),
    position: buildForecastPositionInsight(position),
  };
}
